/**
 * This module provides the BatterySettings higher order component.
 *
 * It defines the layout of the settings sections.
 *
 * And it prepares the consumed form values. The injected form values will be updated as soon as an update request was
 * initiated. This is required to trigger the reactivity behaviour of Formik later on.
 *
 * To ensure the correct loading form state and actual expected reactivity for each component , the follow-up
 * components hierarchy is the following:
 *
 * <BatterySettings> # determines ready-state and what to be displayed for each state
 *   |-><BatterySettingsData> # expects a valid model, prepares initial form values, provides the form processor
 *   | |-><BatterySettingsForm> # provides teh actual form, controls and behaviour
 */
import React from 'react'
import { BatteryModel } from 'src/Libs/batteries'
import { SettingsFormValues } from 'src/Elements/SystemSettings/Battery/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import BatterySettingsData from 'src/Elements/SystemSettings/Battery/BatterySettingsData'
import { makeStyles } from 'tss-react/mui'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'

interface Props {
  model: BatteryModel | null
  onUpdate: (values: SettingsFormValues) => Promise<void>
  isReadOnlyAccess?: boolean
}

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

const BatterySettings: React.FC<Props> = ({ model, onUpdate, isReadOnlyAccess = true }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <AccordionItem
      title={t('systemSettings.battery.settings.sectionTitle')}
      contentClassName={classes.contentAccordion}
      headerContentClassName={classes.headerContent}
    >
      {model ? <BatterySettingsData model={model} onUpdate={onUpdate} isReadOnlyAccess={isReadOnlyAccess} /> : <SystemSettingsBlankView />}
    </AccordionItem>
  )
}

export default BatterySettings
